import React, {useEffect, useMemo, useState} from "react";
import axios from "axios";
import Swal from "sweetalert2";
import {DataGrid, GridToolbar} from "@mui/x-data-grid";
import {baseUrl, ipAddress, userLocation, userName} from "../Univ.js";
import PatientIPSearch from "../PatientIPSearch";
function CSSDManagement() {
    // ============================================================
    // TABS
    // ============================================================
    const [tab, setTab] = useState("issue");
    // ============================================================
    // MASTER DATA
    // ============================================================
    const [departments, setDepartments] = useState([]);
    const [sets, setSets] = useState([]);
    const [instruments, setInstruments] = useState([]);
    const [sterilizations, setSterilizations] = useState([]);
    const [issues, setIssues] = useState([]);
    const [loading, setLoading] = useState(false);
    // ============================================================
    // SELECTED PATIENT
    // ============================================================
    const [patient, setPatient] = useState({});
    // ============================================================
    // CSSD SET MASTER
    // ============================================================
    const [setForm, setSetForm] = useState({
        set_code: "", set_name: "", department: "", instrument_count: 0, status: "ACTIVE"
    });
    // ============================================================
    // INSTRUMENT MASTER
    // ============================================================
    const [instrumentForm, setInstrumentForm] = useState({
        instrument_code: "",
        instrument_name: "",
        category: "",
        set_name: "",
        department: "",
        quantity: 1,
        available_quantity: 1,
        damaged_quantity: 0,
        status: "ACTIVE",
        remarks: ""
    });
    // ============================================================
    // STERILIZATION
    // ============================================================
    const [sterilizationForm, setSterilizationForm] = useState({
        cycle_no: "", // IMPORTANT:
        // This must contain CSSDSet ID
        cssd_set: "", // Auto-filled from CSSD master
        set_name: "",
        department: "",
        instrument_count: "",
        sterilizer: "",
        sterilization_method: "AUTOCLAVE",
        load_no: "",
        temperature: "",
        pressure: "",
        exposure_time: "",
        chemical_indicator: "PASS",
        biological_indicator: "NOT DONE",
        result: "PENDING",
        operator: userName || "",
        remarks: ""
    });
    // ============================================================
    // ISSUE
    // ============================================================
    const [issueForm, setIssueForm] = useState({
        issue_no: "",
        cssd_set: "",
        department: "",
        ip_id: "",
        pat_id: "",
        patient_name: "",
        quantity: 1,
        issued_by: userName || "",
        received_by: "",
        remarks: ""
    });
    // ============================================================
    // RETURN
    // ============================================================
    const [returnForm, setReturnForm] = useState({
        issue_id: "",
        cssd_set: "",
        cssd_set_name: "",
        ip_id: "",
        pat_id: "",
        patient_name: "",
        department: "",
        quantity: 0,
        condition: "GOOD",
        returned_by: userName || "",
        remarks: ""
    });
    // ============================================================
    // LOAD ALL DATA
    // ============================================================
    useEffect(() => {
        loadAllData();
    }, []);
    const loadAllData = () => {
        setLoading(true);
        Promise.all([axios.get(`${baseUrl}add-dept/`)
            .then(response => setDepartments(response.data || []))
            .catch(error => {
                console.log("Department error:", error);
                setDepartments([]);
            }), axios.get(`${baseUrl}cssd/sets/`)
            .then(response => setSets(response.data || []))
            .catch(error => {
                console.log("CSSD Set error:", error);
                setSets([]);
            }), axios.get(`${baseUrl}cssd/instruments/`)
            .then(response => setInstruments(response.data || []))
            .catch(error => {
                console.log("Instrument error:", error);
                setInstruments([]);
            }), axios.get(`${baseUrl}cssd/sterilization/`)
            .then(response => setSterilizations(response.data || []))
            .catch(error => {
                console.log("Sterilization error:", error);
                setSterilizations([]);
            }), axios.get(`${baseUrl}cssd/issues/`)
            .then(response => setIssues(response.data || []))
            .catch(error => {
                console.log("Issue error:", error);
                setIssues([]);
            })])
            .finally(() => {
                setLoading(false);
            });
    };
    // ============================================================
    // DEPARTMENT OPTIONS
    // ============================================================
    const departmentOptions = useMemo(() => {
        return departments
            .filter(item => item.department)
            .map(item => item.department);
    }, [departments]);
    // ============================================================
    // PATIENT SELECT
    // ============================================================
    const selectPatient = (data) => {
        console.log("SELECTED PATIENT:", data);
        setPatient(data || {});
        const patientDepartment = data?.department || data?.dept || data?.department_name || "";
        setIssueForm(prev => ({
            ...prev,
            ip_id: data?.ip_id || data?.ipId || data?.ip?.ip_id || "",
            pat_id: data?.pat_id || data?.patId || data?.patient_id || "",
            patient_name: data?.patient_name || data?.patient || data?.pat?.patient || "",
            department: patientDepartment
        }));
    };
    // ============================================================
    // SET MASTER CHANGE
    // ============================================================
    const handleSetChange = (e) => {
        setSetForm(prev => ({
            ...prev, [e.target.name]: e.target.value
        }));
    };
    // ============================================================
    // SAVE CSSD SET
    // ============================================================
    const saveSet = async (e) => {
        e.preventDefault();
        if (!setForm.set_code || !setForm.set_name) {
            Swal.fire("Required", "Set Code and Set Name are required.", "warning");
            return;
        }
        try {
            setLoading(true);
            await axios.post(`${baseUrl}cssd/sets/`, {
                ...setForm, addedby: userName, ip_add: ipAddress, location: userLocation
            });
            Swal.fire({
                title: "CSSD Set Added",
                text: "CSSD set master saved successfully.",
                icon: "success",
                timer: 1800,
                showConfirmButton: false
            });
            setSetForm({
                set_code: "", set_name: "", department: "", instrument_count: 0, status: "ACTIVE"
            });
            const response = await axios.get(`${baseUrl}cssd/sets/`);
            setSets(response.data || []);
        } catch (error) {
            console.log(error);
            Swal.fire("Error", error?.response?.data?.error || "Unable to save CSSD set.", "error");
        } finally {
            setLoading(false);
        }
    };
    // ============================================================
    // SELECT CSSD SET FOR ISSUE
    // ============================================================
    const selectIssueSet = (e) => {
        const selectedId = e.target.value;
        const selectedSet = sets.find(item => String(item.id) === String(selectedId));
        if (!selectedSet) {
            setIssueForm(prev => ({
                ...prev, cssd_set: "", department: ""
            }));
            return;
        }
        setIssueForm(prev => ({
            ...prev, cssd_set: selectedSet.id, department: selectedSet.department || ""
        }));
    };
    // ============================================================
    // ISSUE FORM CHANGE
    // ============================================================
    const handleIssueChange = (e) => {
        setIssueForm(prev => ({
            ...prev, [e.target.name]: e.target.value
        }));
    };
    // ============================================================
    // ISSUE CSSD SET
    // ============================================================
    const submitIssue = async () => {
        if (!issueForm.cssd_set) {
            Swal.fire("Required", "Please select a CSSD Set.", "warning");
            return;
        }
        if (!issueForm.department) {
            Swal.fire("Required", "Please select a department.", "warning");
            return;
        }
        if (!issueForm.ip_id) {
            Swal.fire("Required", "Please select an IP Patient.", "warning");
            return;
        }
        if (!issueForm.received_by) {
            Swal.fire("Required", "Please enter/select the receiving staff.", "warning");
            return;
        }
        try {
            const fd = new FormData();
            const issueNo = "CSSD-ISS-" + new Date().getTime();
            fd.append("issue_no", issueNo);
            /*
            =====================================================
            CSSD SET
            IMPORTANT:
            Send CSSDSet primary key
            =====================================================
            */
            fd.append("cssd_set", issueForm.cssd_set);
            fd.append("department", issueForm.department);
            fd.append("ip_id", issueForm.ip_id);
            fd.append("pat_id", issueForm.pat_id);
            fd.append("patient_name", issueForm.patient_name);
            fd.append("quantity", issueForm.quantity);
            fd.append("issued_by", userName);
            fd.append("received_by", issueForm.received_by);
            fd.append("remarks", issueForm.remarks);
            fd.append("status", "ISSUED");
            fd.append("ip_add", ipAddress);
            fd.append("location", userLocation);
            await axios.post(`${baseUrl}cssd/issues/`, fd);
            Swal.fire({
                title: "CSSD Set Issued",
                text: "CSSD set issued successfully.",
                icon: "success",
                timer: 2000,
                showConfirmButton: false
            });
            setIssueForm({
                issue_no: "",
                cssd_set: "",
                department: "",
                ip_id: "",
                pat_id: "",
                patient_name: "",
                quantity: 1,
                issued_by: userName || "",
                received_by: "",
                remarks: ""
            });
            setPatient({});
            loadAllData();
        } catch (error) {
            console.log("CSSD ISSUE ERROR:", error?.response?.data || error);
            Swal.fire("Error", error?.response?.data?.error || "Unable to issue CSSD set.", "error");
        }
    };
    // ============================================================
    // SELECT ISSUE FOR RETURN
    // ============================================================
    const selectReturnIssue = (e) => {
        const issueId = e.target.value;
        // Clear form
        if (!issueId) {
            setReturnForm({
                issue_id: "",
                cssd_set: "",
                cssd_set_name: "",
                ip_id: "",
                pat_id: "",
                patient_name: "",
                department: "",
                quantity: 0,
                condition: "GOOD",
                returned_by: userName || "",
                remarks: ""
            });
            return;
        }
        // Find selected issued record
        const selectedIssue = issues.find(item => String(item.id) === String(issueId));
        if (!selectedIssue) {
            console.log("ISSUE NOT FOUND:", issueId);
            return;
        }
        console.log("SELECTED ISSUE:", selectedIssue);
        /*
        =========================================================
        GET CSSD SET ID FROM ISSUE
        =========================================================
        */
        let cssdSetId = selectedIssue.cssd_set;
        /*
        Sometimes Django serializer may return:

        cssd_set: 1

        OR:

        cssd_set: {
            id: 1,
            set_name: "Major Surgery Set"
        }
        */
        if (cssdSetId && typeof cssdSetId === "object") {
            cssdSetId = cssdSetId.id;
        }
        /*
        =========================================================
        FIND CSSD SET IN MASTER
        =========================================================
        */
        const selectedSet = sets.find(item => String(item.id) === String(cssdSetId));
        console.log("CSSD SET ID:", cssdSetId);
        console.log("CSSD SET MASTER RECORD:", selectedSet);
        /*
        =========================================================
        SET RETURN FORM
        =========================================================
        */
        setReturnForm({
            issue_id: selectedIssue.id, /*
        Database FK
        */

            cssd_set: cssdSetId || "", /*
        Display name from CSSD master
        */

            cssd_set_name: selectedSet ? `${selectedSet.set_code} - ${selectedSet.set_name}` : "",
            ip_id: selectedIssue.ip_id || "",
            pat_id: selectedIssue.pat_id || "",
            patient_name: selectedIssue.patient_name || "",
            department: selectedIssue.department || (selectedSet ? selectedSet.department : "") || "",
            quantity: selectedIssue.quantity || 1,
            condition: "GOOD",
            returned_by: userName || "",
            remarks: ""
        });
    };
    // ============================================================
    // RETURN CSSD SET
    // ============================================================
    const submitReturn = async () => {
        if (!returnForm.issue_id) {
            Swal.fire("Required", "Please select an issued CSSD set.", "warning");
            return;
        }
        try {
            const payload = {
                condition: returnForm.condition || "GOOD",
                returned_by: userName || "",
                remarks: returnForm.remarks || "",
                ip_add: ipAddress || "",
                location: userLocation || ""
            };
            await axios.post(`${baseUrl}cssd/issues/${returnForm.issue_id}/return/`, payload);
            await Swal.fire({
                title: "CSSD Set Returned",
                text: `${returnForm.cssd_set_name || returnForm.instrument_set} returned successfully.`,
                icon: "success",
                timer: 1800,
                showConfirmButton: false
            });
            setReturnForm({
                issue_id: "",
                issue_no: "",
                ip_id: "",
                pat_id: "",
                patient_name: "",
                cssd_set_name: "",
                instrument_set: "",
                department: "",
                quantity: "",
                condition: "GOOD",
                returned_by: userName || "",
                remarks: ""
            });
            loadAllData();
        } catch (error) {
            console.log("CSSD RETURN ERROR:", error);
            Swal.fire("Error", error?.response?.data?.error || "Unable to return CSSD set.", "error");
        }
    };
    // ============================================================
    // STERILIZATION SET SELECT
    // ============================================================
    const selectSterilizationSet = (e) => {
        const selectedId = e.target.value;
        const selectedSet = sets.find(item => String(item.id) === String(selectedId));
        if (!selectedSet) {
            setSterilizationForm(prev => ({
                ...prev, cssd_set: "", set_name: "", department: "", instrument_count: ""
            }));
            return;
        }
        setSterilizationForm(prev => ({
            ...prev, // Actual ForeignKey value sent to Django
            cssd_set: selectedSet.id, // Display / backend convenience values
            set_name: selectedSet.set_name || "",
            department: selectedSet.department || "",
            instrument_count: selectedSet.instrument_count || 0
        }));
    };
    // ============================================================
    // STERILIZATION CHANGE
    // ============================================================
    const handleSterilizationChange = e => {
        setSterilizationForm(prev => ({
            ...prev, [e.target.name]: e.target.value
        }));
    };
    // ============================================================
    // SAVE STERILIZATION
    // ============================================================
    const submitSterilization = async e => {
        e.preventDefault();
        if (!sterilizationForm.cssd_set) {
            Swal.fire("Required", "Please select CSSD Set.", "warning");
            return;
        }
        if (!sterilizationForm.sterilizer) {
            Swal.fire("Required", "Please select Sterilizer.", "warning");
            return;
        }
        if (!sterilizationForm.load_no) {
            Swal.fire("Required", "Please enter Load Number.", "warning");
            return;
        }
        try {
            const fd = new FormData();
            /*
            ==========================================
            CSSD MASTER RELATION
            ==========================================
            */
            fd.append("cssd_set", sterilizationForm.cssd_set);
            /*
            ==========================================
            OTHER DATA
            ==========================================
            */
            fd.append("cycle_no", sterilizationForm.cycle_no);
            fd.append("set_name", sterilizationForm.set_name);
            fd.append("department", sterilizationForm.department);
            fd.append("instrument_count", sterilizationForm.instrument_count);
            fd.append("sterilizer", sterilizationForm.sterilizer);
            fd.append("sterilization_method", sterilizationForm.sterilization_method);
            fd.append("load_no", sterilizationForm.load_no);
            fd.append("temperature", sterilizationForm.temperature);
            fd.append("pressure", sterilizationForm.pressure);
            fd.append("exposure_time", sterilizationForm.exposure_time);
            fd.append("chemical_indicator", sterilizationForm.chemical_indicator);
            fd.append("biological_indicator", sterilizationForm.biological_indicator);
            fd.append("result", sterilizationForm.result);
            fd.append("operator", sterilizationForm.operator);
            fd.append("remarks", sterilizationForm.remarks);
            /*
            ==========================================
            AUDIT
            ==========================================
            */
            fd.append("addedby", userName || "");
            fd.append("ip_add", ipAddress || "");
            fd.append("location", userLocation || "");
            /*
            ==========================================
            DEBUG
            ==========================================
            */
            console.log("CSSD STERILIZATION DATA");
            for (const pair of fd.entries()) {
                console.log(pair[0], pair[1]);
            }
            /*
            ==========================================
            SAVE
            ==========================================
            */
            await axios.post(`${baseUrl}cssd/sterilization/`, fd);
            /*
            ==========================================
            SUCCESS
            ==========================================
            */
            await Swal.fire({
                title: "Sterilization Saved",
                text: "Sterilization cycle recorded successfully.",
                icon: "success",
                timer: 1800,
                showConfirmButton: false
            });
            /*
            ==========================================
            RESET
            ==========================================
            */
            setSterilizationForm({
                cycle_no: "",
                cssd_set: "",
                set_name: "",
                department: "",
                instrument_count: "",
                sterilizer: "",
                sterilization_method: "AUTOCLAVE",
                load_no: "",
                temperature: "",
                pressure: "",
                exposure_time: "",
                chemical_indicator: "PASS",
                biological_indicator: "NOT DONE",
                result: "PENDING",
                operator: userName || "",
                remarks: ""
            });
            /*
            ==========================================
            REFRESH
            ==========================================
            */
            loadAllData();
        } catch (error) {
            console.log("CSSD STERILIZATION ERROR:", error);
            console.log("SERVER RESPONSE:", error?.response?.data);
            Swal.fire("Error", error?.response?.data?.error || JSON.stringify(error?.response?.data || {}) || "Unable to save sterilization.", "error");
        }
    };
    // ============================================================
    // INSTRUMENT MASTER CHANGE
    // ============================================================
    const handleInstrumentChange = e => {
        const {name, value} = e.target;
        setInstrumentForm(prev => ({
            ...prev, [name]: value
        }));
        if (name === "set_name") {
            const selectedSet = sets.find(item => item.set_name === value);
            if (selectedSet) {
                setInstrumentForm(prev => ({
                    ...prev, set_name: selectedSet.set_name, department: selectedSet.department || ""
                }));
            }
        }
    };
    // ============================================================
    // SAVE INSTRUMENT
    // ============================================================
    const submitInstrument = async e => {
        e.preventDefault();
        if (!instrumentForm.instrument_code || !instrumentForm.instrument_name) {
            Swal.fire("Required", "Instrument Code and Instrument Name are required.", "warning");
            return;
        }
        try {
            await axios.post(`${baseUrl}cssd/instruments/`, {
                ...instrumentForm,
                available_quantity: instrumentForm.quantity,
                addedby: userName,
                ip_add: ipAddress,
                location: userLocation
            });
            Swal.fire({
                title: "Instrument Added",
                text: "CSSD instrument saved successfully.",
                icon: "success",
                timer: 1800,
                showConfirmButton: false
            });
            setInstrumentForm({
                instrument_code: "",
                instrument_name: "",
                category: "",
                set_name: "",
                department: "",
                quantity: 1,
                available_quantity: 1,
                damaged_quantity: 0,
                status: "ACTIVE",
                remarks: ""
            });
            loadAllData();
        } catch (error) {
            console.log(error);
            Swal.fire("Error", error?.response?.data?.error || "Unable to save instrument.", "error");
        }
    };
    // ============================================================
    // ISSUE COLUMNS
    // ============================================================
    const issueColumns = [{
        field: "issue_no", headerName: "Issue No", width: 160
    }, {
        field: "cssd_set", headerName: "CSSD Set", width: 220
    }, {
        field: "ip_id", headerName: "IP ID", width: 140
    }, {
        field: "pat_id", headerName: "Patient ID", width: 150
    }, {
        field: "patient_name", headerName: "Patient", width: 200
    }, {
        field: "department", headerName: "Department", width: 180
    }, {
        field: "quantity", headerName: "Qty", width: 80
    }, {
        field: "issued_by", headerName: "Issued By", width: 150
    }, {
        field: "received_by", headerName: "Received By", width: 160
    }, {
        field: "status", headerName: "Status", width: 120
    }];
    // ============================================================
    // STERILIZATION COLUMNS
    // ============================================================
    const sterilizationColumns = [{
        field: "cycle_no", headerName: "Cycle No", width: 140
    }, {
        field: "set_name", headerName: "CSSD Set", width: 200
    }, {
        field: "department", headerName: "Department", width: 170
    }, {
        field: "sterilizer", headerName: "Sterilizer", width: 150
    }, {
        field: "sterilization_method", headerName: "Method", width: 150
    }, {
        field: "temperature", headerName: "Temperature", width: 130
    }, {
        field: "pressure", headerName: "Pressure", width: 120
    }, {
        field: "exposure_time", headerName: "Exposure", width: 120
    }, {
        field: "result", headerName: "Result", width: 120
    }, {
        field: "operator", headerName: "Operator", width: 150
    }];
    // ============================================================
    // SET MASTER COLUMNS
    // ============================================================
    const setColumns = [{
        field: "id", headerName: "ID", width: 80
    }, {
        field: "set_code", headerName: "Set Code", width: 150
    }, {
        field: "set_name", headerName: "CSSD Set", width: 250
    }, {
        field: "department", headerName: "Department", width: 180
    }, {
        field: "instrument_count", headerName: "Instrument Count", width: 150
    }, {
        field: "status", headerName: "Status", width: 120
    }, {
        field: "addedby", headerName: "Added By", width: 150
    }];
    // ============================================================
    // INSTRUMENT COLUMNS
    // ============================================================
    const instrumentColumns = [{
        field: "instrument_code", headerName: "Code", width: 140
    }, {
        field: "instrument_name", headerName: "Instrument", width: 220
    }, {
        field: "category", headerName: "Category", width: 160
    }, {
        field: "set_name", headerName: "CSSD Set", width: 200
    }, {
        field: "department", headerName: "Department", width: 170
    }, {
        field: "quantity", headerName: "Total", width: 100
    }, {
        field: "available_quantity", headerName: "Available", width: 110
    }, {
        field: "damaged_quantity", headerName: "Damaged", width: 110
    }, {
        field: "status", headerName: "Status", width: 120
    }];
    // ============================================================
    // RENDER
    // ============================================================
    return (<section className="container-fluid mt-4">

        {/* ================================================== */}
        {/* HEADER */}
        {/* ================================================== */}

        <div className="card shadow-sm mb-3">

            <div className="card-header bg-primary text-white">

                <h4 className="mb-0">

                    <i className="fa fa-syringe me-2"></i>

                    CSSD Management

                </h4>

            </div>

        </div>


        {/* ================================================== */}
        {/* TABS */}
        {/* ================================================== */}

        <div className="btn-group mb-4 flex-wrap">

            <button
                className={`btn ${tab === "master" ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => setTab("master")}
            >

                <i className="fa fa-layer-group me-1"></i>

                Set Master

            </button>


            <button
                className={`btn ${tab === "instrument" ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => setTab("instrument")}
            >

                <i className="fa fa-toolbox me-1"></i>

                Instruments

            </button>


            <button
                className={`btn ${tab === "sterilization" ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => setTab("sterilization")}
            >

                <i className="fa fa-temperature-high me-1"></i>

                Sterilization

            </button>


            <button
                className={`btn ${tab === "issue" ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => setTab("issue")}
            >

                <i className="fa fa-arrow-right me-1"></i>

                Issue

            </button>


            <button
                className={`btn ${tab === "return" ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => setTab("return")}
            >

                <i className="fa fa-rotate-left me-1"></i>

                Return

            </button>


            <button
                className={`btn ${tab === "stock" ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => setTab("stock")}
            >

                <i className="fa fa-boxes-stacked me-1"></i>

                Stock

            </button>


            <button
                className={`btn ${tab === "history" ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => setTab("history")}
            >

                <i className="fa fa-history me-1"></i>

                History

            </button>

        </div>


        {/* ================================================== */}
        {/* SET MASTER */}
        {/* ================================================== */}

        {tab === "master" && (<>

            <div className="card shadow-sm mb-4">

                <div className="card-header bg-primary text-white">

                    <h5 className="mb-0">

                        <i className="fa fa-layer-group me-2"></i>

                        CSSD Set Master

                    </h5>

                </div>


                <div className="card-body">

                    <form onSubmit={saveSet}>

                        <div className="row">

                            <div className="col-md-2 mb-3">

                                <label className="form-label">
                                    Set Code
                                </label>

                                <input
                                    className="form-control"
                                    name="set_code"
                                    value={setForm.set_code}
                                    onChange={handleSetChange}
                                    placeholder="CSSD-SET-001"
                                    required
                                />

                            </div>


                            <div className="col-md-3 mb-3">

                                <label className="form-label">
                                    Set Name
                                </label>

                                <input
                                    className="form-control"
                                    name="set_name"
                                    value={setForm.set_name}
                                    onChange={handleSetChange}
                                    placeholder="General Surgery Set"
                                    required
                                />

                            </div>


                            <div className="col-md-3 mb-3">

                                <label className="form-label">
                                    Department
                                </label>

                                <select
                                    className="form-control"
                                    name="department"
                                    value={setForm.department}
                                    onChange={handleSetChange}
                                >

                                    <option value="">
                                        Select Department
                                    </option>

                                    {departmentOptions.map(department => (<option
                                        key={department}
                                        value={department}
                                    >
                                        {department}
                                    </option>))}

                                </select>

                            </div>


                            <div className="col-md-2 mb-3">

                                <label className="form-label">
                                    Instrument Count
                                </label>

                                <input
                                    type="number"
                                    min="0"
                                    className="form-control"
                                    name="instrument_count"
                                    value={setForm.instrument_count}
                                    onChange={handleSetChange}
                                />

                            </div>


                            <div className="col-md-2 mb-3">

                                <label className="form-label">
                                    Status
                                </label>

                                <select
                                    className="form-control"
                                    name="status"
                                    value={setForm.status}
                                    onChange={handleSetChange}
                                >

                                    <option value="ACTIVE">
                                        ACTIVE
                                    </option>

                                    <option value="INACTIVE">
                                        INACTIVE
                                    </option>

                                </select>

                            </div>

                        </div>


                        <button
                            type="submit"
                            className="btn btn-success"
                            disabled={loading}
                        >

                            <i className="fa fa-save me-1"></i>

                            Save CSSD Set

                        </button>

                    </form>

                </div>

            </div>


            <DataGrid
                rows={sets}
                columns={setColumns}
                autoHeight
                loading={loading}
                slots={{
                    toolbar: GridToolbar
                }}
                pageSizeOptions={[10, 25, 50]}
            />

        </>)}


        {/* ================================================== */}
        {/* INSTRUMENT MASTER */}
        {/* ================================================== */}

        {tab === "instrument" && (<>

            <div className="card shadow-sm mb-4">

                <div className="card-header bg-info text-white">

                    <h5 className="mb-0">

                        <i className="fa fa-toolbox me-2"></i>

                        CSSD Instrument Master

                    </h5>

                </div>


                <div className="card-body">

                    <form onSubmit={submitInstrument}>

                        <div className="row">

                            <div className="col-md-2 mb-3">

                                <label>
                                    Instrument Code
                                </label>

                                <input
                                    className="form-control"
                                    name="instrument_code"
                                    value={instrumentForm.instrument_code}
                                    onChange={handleInstrumentChange}
                                    placeholder="INS-001"
                                    required
                                />

                            </div>


                            <div className="col-md-3 mb-3">

                                <label>
                                    Instrument Name
                                </label>

                                <input
                                    className="form-control"
                                    name="instrument_name"
                                    value={instrumentForm.instrument_name}
                                    onChange={handleInstrumentChange}
                                    placeholder="Artery Forceps"
                                    required
                                />

                            </div>


                            <div className="col-md-2 mb-3">

                                <label>
                                    Category
                                </label>

                                <select
                                    className="form-control"
                                    name="category"
                                    value={instrumentForm.category}
                                    onChange={handleInstrumentChange}
                                >

                                    <option value="">
                                        Select Category
                                    </option>

                                    <option value="SURGICAL">
                                        Surgical
                                    </option>

                                    <option value="DENTAL">
                                        Dental
                                    </option>

                                    <option value="ORTHOPAEDIC">
                                        Orthopaedic
                                    </option>

                                    <option value="OBG">
                                        OBG
                                    </option>

                                    <option value="ENT">
                                        ENT
                                    </option>

                                    <option value="GENERAL">
                                        General
                                    </option>

                                </select>

                            </div>


                            <div className="col-md-3 mb-3">

                                <label>
                                    CSSD Set
                                </label>

                                <select
                                    className="form-control"
                                    name="set_name"
                                    value={instrumentForm.set_name}
                                    onChange={handleInstrumentChange}
                                >

                                    <option value="">
                                        Select CSSD Set
                                    </option>

                                    {sets.map(item => (<option
                                        key={item.id}
                                        value={item.set_name}
                                    >

                                        {item.set_code}
                                        {" - "}
                                        {item.set_name}

                                    </option>))}

                                </select>

                            </div>


                            <div className="col-md-2 mb-3">

                                <label>
                                    Department
                                </label>

                                <input
                                    className="form-control"
                                    value={instrumentForm.department}
                                    readOnly
                                />

                            </div>


                            <div className="col-md-2 mb-3">

                                <label>
                                    Quantity
                                </label>

                                <input
                                    type="number"
                                    min="1"
                                    className="form-control"
                                    name="quantity"
                                    value={instrumentForm.quantity}
                                    onChange={handleInstrumentChange}
                                />

                            </div>


                            <div className="col-md-2 mb-3">

                                <label>
                                    Damaged Qty
                                </label>

                                <input
                                    type="number"
                                    min="0"
                                    className="form-control"
                                    name="damaged_quantity"
                                    value={instrumentForm.damaged_quantity}
                                    onChange={handleInstrumentChange}
                                />

                            </div>


                            <div className="col-md-4 mb-3">

                                <label>
                                    Remarks
                                </label>

                                <input
                                    className="form-control"
                                    name="remarks"
                                    value={instrumentForm.remarks}
                                    onChange={handleInstrumentChange}
                                />

                            </div>

                        </div>


                        <button
                            type="submit"
                            className="btn btn-success"
                        >

                            <i className="fa fa-save me-1"></i>

                            Save Instrument

                        </button>

                    </form>

                </div>

            </div>


            <DataGrid
                rows={instruments}
                columns={instrumentColumns}
                autoHeight
                slots={{
                    toolbar: GridToolbar
                }}
                pageSizeOptions={[10, 25, 50]}
            />

        </>)}


        {/* ================================================== */}
        {/* STERILIZATION */}
        {/* ================================================== */}

        {tab === "sterilization" && (<>

            <div className="card shadow-sm mb-4">

                <div className="card-header bg-success text-white">

                    <h5 className="mb-0">

                        <i className="fa fa-temperature-high me-2"></i>

                        Sterilization Cycle

                    </h5>

                </div>


                <div className="card-body">

                    <form onSubmit={submitSterilization}>

                        <div className="row">

                            {/* ==========================================
            CYCLE NUMBER
        ========================================== */}

                            <div className="col-md-2 mb-3">

                                <label>
                                    Cycle No
                                </label>

                                <input
                                    className="form-control"
                                    name="cycle_no"
                                    value={sterilizationForm.cycle_no}
                                    onChange={handleSterilizationChange}
                                    placeholder="AUTO-001"
                                />

                            </div>


                            {/* ==========================================
            CSSD SET
        ========================================== */}

                            <div className="col-md-3 mb-3">

                                <label>
                                    CSSD Set
                                </label>

                                <select
                                    className="form-control"
                                    value={sterilizationForm.cssd_set}
                                    onChange={selectSterilizationSet}
                                    required
                                >

                                    <option value="">
                                        Select CSSD Set
                                    </option>

                                    {sets.map(item => (<option
                                        key={item.id}
                                        value={item.id}
                                    >

                                        {item.set_code}
                                        {" - "}
                                        {item.set_name}

                                    </option>))}

                                </select>

                            </div>


                            {/* ==========================================
            SET NAME
        ========================================== */}

                            <div className="col-md-3 mb-3">

                                <label>
                                    Set Name
                                </label>

                                <input
                                    className="form-control"
                                    value={sterilizationForm.set_name}
                                    readOnly
                                />

                            </div>


                            {/* ==========================================
            DEPARTMENT
        ========================================== */}

                            <div className="col-md-2 mb-3">

                                <label>
                                    Department
                                </label>

                                <input
                                    className="form-control"
                                    value={sterilizationForm.department}
                                    readOnly
                                />

                            </div>


                            {/* ==========================================
            INSTRUMENT COUNT
        ========================================== */}

                            <div className="col-md-2 mb-3">

                                <label>
                                    Instruments
                                </label>

                                <input
                                    type="number"
                                    className="form-control"
                                    value={sterilizationForm.instrument_count}
                                    readOnly
                                />

                            </div>


                            {/* ==========================================
            STERILIZER
        ========================================== */}

                            <div className="col-md-3 mb-3">

                                <label>
                                    Sterilizer
                                </label>

                                <select
                                    className="form-control"
                                    name="sterilizer"
                                    value={sterilizationForm.sterilizer}
                                    onChange={handleSterilizationChange}
                                    required
                                >

                                    <option value="">
                                        Select Sterilizer
                                    </option>

                                    <option value="AUTOCLAVE-01">
                                        Autoclave 01
                                    </option>

                                    <option value="AUTOCLAVE-02">
                                        Autoclave 02
                                    </option>

                                    <option value="ETO-01">
                                        ETO Sterilizer
                                    </option>

                                    <option value="PLASMA-01">
                                        Plasma Sterilizer
                                    </option>

                                    <option value="DRY-HEAT-01">
                                        Dry Heat Sterilizer
                                    </option>

                                </select>

                            </div>


                            {/* ==========================================
            METHOD
        ========================================== */}

                            <div className="col-md-3 mb-3">

                                <label>
                                    Sterilization Method
                                </label>

                                <select
                                    className="form-control"
                                    name="sterilization_method"
                                    value={sterilizationForm.sterilization_method}
                                    onChange={handleSterilizationChange}
                                >

                                    <option value="AUTOCLAVE">
                                        Autoclave
                                    </option>

                                    <option value="ETO">
                                        ETO
                                    </option>

                                    <option value="PLASMA">
                                        Plasma
                                    </option>

                                    <option value="DRY HEAT">
                                        Dry Heat
                                    </option>

                                </select>

                            </div>


                            {/* ==========================================
            LOAD
        ========================================== */}

                            <div className="col-md-2 mb-3">

                                <label>
                                    Load No
                                </label>

                                <input
                                    className="form-control"
                                    name="load_no"
                                    value={sterilizationForm.load_no}
                                    onChange={handleSterilizationChange}
                                    placeholder="LOAD-001"
                                    required
                                />

                            </div>


                            {/* ==========================================
            TEMPERATURE
        ========================================== */}

                            <div className="col-md-2 mb-3">

                                <label>
                                    Temperature
                                </label>

                                <input
                                    className="form-control"
                                    name="temperature"
                                    value={sterilizationForm.temperature}
                                    onChange={handleSterilizationChange}
                                    placeholder="121 °C"
                                />

                            </div>


                            {/* ==========================================
            PRESSURE
        ========================================== */}

                            <div className="col-md-2 mb-3">

                                <label>
                                    Pressure
                                </label>

                                <input
                                    className="form-control"
                                    name="pressure"
                                    value={sterilizationForm.pressure}
                                    onChange={handleSterilizationChange}
                                    placeholder="15 PSI"
                                />

                            </div>


                            {/* ==========================================
            EXPOSURE
        ========================================== */}

                            <div className="col-md-3 mb-3">

                                <label>
                                    Exposure Time
                                </label>

                                <input
                                    className="form-control"
                                    name="exposure_time"
                                    value={sterilizationForm.exposure_time}
                                    onChange={handleSterilizationChange}
                                    placeholder="30 Minutes"
                                />

                            </div>


                            {/* ==========================================
            CHEMICAL INDICATOR
        ========================================== */}

                            <div className="col-md-3 mb-3">

                                <label>
                                    Chemical Indicator
                                </label>

                                <select
                                    className="form-control"
                                    name="chemical_indicator"
                                    value={sterilizationForm.chemical_indicator}
                                    onChange={handleSterilizationChange}
                                >

                                    <option value="PASS">
                                        PASS
                                    </option>

                                    <option value="FAIL">
                                        FAIL
                                    </option>

                                </select>

                            </div>


                            {/* ==========================================
            BIOLOGICAL INDICATOR
        ========================================== */}

                            <div className="col-md-3 mb-3">

                                <label>
                                    Biological Indicator
                                </label>

                                <select
                                    className="form-control"
                                    name="biological_indicator"
                                    value={sterilizationForm.biological_indicator}
                                    onChange={handleSterilizationChange}
                                >

                                    <option value="NOT DONE">
                                        NOT DONE
                                    </option>

                                    <option value="PASS">
                                        PASS
                                    </option>

                                    <option value="FAIL">
                                        FAIL
                                    </option>

                                </select>

                            </div>


                            {/* ==========================================
            RESULT
        ========================================== */}

                            <div className="col-md-3 mb-3">

                                <label>
                                    Result
                                </label>

                                <select
                                    className="form-control"
                                    name="result"
                                    value={sterilizationForm.result}
                                    onChange={handleSterilizationChange}
                                >

                                    <option value="PENDING">
                                        PENDING
                                    </option>

                                    <option value="PASS">
                                        PASS
                                    </option>

                                    <option value="FAIL">
                                        FAIL
                                    </option>

                                </select>

                            </div>


                            {/* ==========================================
            OPERATOR
        ========================================== */}

                            <div className="col-md-3 mb-3">

                                <label>
                                    Operator
                                </label>

                                <input
                                    className="form-control"
                                    name="operator"
                                    value={sterilizationForm.operator}
                                    onChange={handleSterilizationChange}
                                />

                            </div>


                            {/* ==========================================
            REMARKS
        ========================================== */}

                            <div className="col-md-9 mb-3">

                                <label>
                                    Remarks
                                </label>

                                <input
                                    className="form-control"
                                    name="remarks"
                                    value={sterilizationForm.remarks}
                                    onChange={handleSterilizationChange}
                                />

                            </div>

                        </div>


                        <button
                            type="submit"
                            className="btn btn-primary"
                        >

                            <i className="fa fa-check me-2"></i>

                            Save Sterilization Cycle

                        </button>

                    </form>

                </div>

            </div>


            <DataGrid
                rows={sterilizations}
                columns={sterilizationColumns}
                autoHeight
                slots={{
                    toolbar: GridToolbar
                }}
                pageSizeOptions={[10, 25, 50]}
            />

        </>)}


        {/* ================================================== */}
        {/* ISSUE */}
        {/* ================================================== */}

        {tab === "issue" && (<>

            <PatientIPSearch
                onSelect={selectPatient}
            />


            <div className="card shadow-sm mt-3 mb-4">

                <div className="card-header bg-warning">

                    <h5 className="mb-0">

                        <i className="fa fa-arrow-right me-2"></i>

                        Issue CSSD Set

                    </h5>

                </div>


                <div className="card-body">

                    <div className="row">

                        <div className="col-md-3 mb-3">

                            <label>
                                IP ID
                            </label>

                            <input
                                className="form-control"
                                value={issueForm.ip_id}
                                readOnly
                            />

                        </div>


                        <div className="col-md-3 mb-3">

                            <label>
                                Patient ID
                            </label>

                            <input
                                className="form-control"
                                value={issueForm.pat_id}
                                readOnly
                            />

                        </div>


                        <div className="col-md-3 mb-3">

                            <label>
                                Patient
                            </label>

                            <input
                                className="form-control"
                                value={issueForm.patient_name}
                                readOnly
                            />

                        </div>


                        <div className="col-md-3 mb-3">

                            <label>
                                Department
                            </label>

                            <select
                                className="form-control"
                                name="department"
                                value={issueForm.department}
                                onChange={handleIssueChange}
                            >

                                <option value="">
                                    Select Department
                                </option>

                                {departmentOptions.map(department => (<option
                                    key={department}
                                    value={department}
                                >
                                    {department}
                                </option>))}

                            </select>

                        </div>

                        <div className="col-md-4 mb-3">

                            <label>
                                CSSD Set
                            </label>

                            <select
                                className="form-control"
                                name="cssd_set"
                                value={issueForm.cssd_set}
                                onChange={selectIssueSet}
                            >

                                <option value="">
                                    Select CSSD Set
                                </option>

                                {sets
                                    .filter(item => item.status === "ACTIVE" || !item.status)
                                    .map(item => (<option
                                        key={item.id}
                                        value={item.id}
                                    >

                                        {item.set_code}
                                        {" - "}
                                        {item.set_name}

                                    </option>))}

                            </select>

                        </div>
                        <div className="col-md-2 mb-3">

                            <label>
                                Quantity
                            </label>

                            <input
                                type="number"
                                min="1"
                                className="form-control"
                                name="quantity"
                                value={issueForm.quantity}
                                onChange={handleIssueChange}
                            />

                        </div>


                        <div className="col-md-3 mb-3">

                            <label>
                                Received By
                            </label>

                            <input
                                className="form-control"
                                name="received_by"
                                value={issueForm.received_by}
                                onChange={handleIssueChange}
                                placeholder="Staff / Nurse"
                            />

                        </div>


                        <div className="col-md-3 mb-3">

                            <label>
                                Remarks
                            </label>

                            <input
                                className="form-control"
                                name="remarks"
                                value={issueForm.remarks}
                                onChange={handleIssueChange}
                            />

                        </div>

                    </div>


                    <div className="alert alert-info">

                        <i className="fa fa-info-circle me-2"></i>

                        Patient details are automatically populated
                        from the IP/Patient search. CSSD Set selection
                        automatically determines the mapped department.

                    </div>


                    <button
                        className="btn btn-success"
                        onClick={submitIssue}
                    >

                        <i className="fa fa-arrow-right me-1"></i>

                        Issue CSSD Set

                    </button>

                </div>

            </div>


            <DataGrid
                rows={issues}
                columns={issueColumns}
                autoHeight
                slots={{
                    toolbar: GridToolbar
                }}
                pageSizeOptions={[10, 25, 50]}
            />

        </>)}


        {/* ================================================== */}
        {/* RETURN */}
        {/* ================================================== */}

        {tab === "return" && (<div className="card shadow-sm">

            <div className="card-header bg-danger text-white">

                <h5 className="mb-0">

                    <i className="fa fa-rotate-left me-2"></i>

                    CSSD Set Return

                </h5>

            </div>


            <div className="card-body">

                <div className="row">

                    {/* =========================================
                    ISSUED CSSD RECORD
                ========================================= */}

                    <div className="col-md-6 mb-3">

                        <label>
                            Issued CSSD Record
                        </label>

                        <select
                            className="form-control"
                            value={returnForm.issue_id}
                            onChange={selectReturnIssue}
                        >

                            <option value="">
                                Select Issued CSSD Set
                            </option>

                            {issues
                                .filter(item => item.status === "ISSUED")
                                .map(item => {
                                    let cssdSetId = item.cssd_set;
                                    if (cssdSetId && typeof cssdSetId === "object") {
                                        cssdSetId = cssdSetId.id;
                                    }
                                    const cssdSet = sets.find(s => String(s.id) === String(cssdSetId));
                                    return (<option
                                        key={item.id}
                                        value={item.id}
                                    >

                                        {item.issue_no}

                                        {" | "}

                                        {cssdSet ? `${cssdSet.set_code} - ${cssdSet.set_name}` : "CSSD Set"}

                                        {" | "}

                                        {item.patient_name || item.department || ""}

                                    </option>);
                                })}

                        </select>

                    </div>


                    {/* =========================================
                    IP ID
                ========================================= */}

                    <div className="col-md-3 mb-3">

                        <label>
                            IP ID
                        </label>

                        <input
                            className="form-control"
                            value={returnForm.ip_id || ""}
                            readOnly
                        />

                    </div>


                    {/* =========================================
                    PATIENT ID
                ========================================= */}

                    <div className="col-md-3 mb-3">

                        <label>
                            Patient ID
                        </label>

                        <input
                            className="form-control"
                            value={returnForm.pat_id || ""}
                            readOnly
                        />

                    </div>


                    {/* =========================================
                    PATIENT
                ========================================= */}

                    <div className="col-md-4 mb-3">

                        <label>
                            Patient
                        </label>

                        <input
                            className="form-control"
                            value={returnForm.patient_name || ""}
                            readOnly
                        />

                    </div>


                    {/* =========================================
                    CSSD SET
                ========================================= */}

                    <div className="col-md-4 mb-3">

                        <label>
                            CSSD Set
                        </label>

                        <input
                            className="form-control"
                            value={returnForm.cssd_set_name || ""}
                            readOnly
                        />

                    </div>


                    {/* =========================================
                    DEPARTMENT
                ========================================= */}

                    <div className="col-md-2 mb-3">

                        <label>
                            Department
                        </label>

                        <input
                            className="form-control"
                            value={returnForm.department || ""}
                            readOnly
                        />

                    </div>


                    {/* =========================================
                    QUANTITY
                ========================================= */}

                    <div className="col-md-2 mb-3">

                        <label>
                            Quantity
                        </label>

                        <input
                            type="number"
                            className="form-control"
                            value={returnForm.quantity || 0}
                            readOnly
                        />

                    </div>


                    {/* =========================================
                    CONDITION
                ========================================= */}

                    <div className="col-md-3 mb-3">

                        <label>
                            Condition
                        </label>

                        <select
                            className="form-control"
                            value={returnForm.condition}
                            onChange={e => setReturnForm(prev => ({
                                ...prev, condition: e.target.value
                            }))}
                        >

                            <option value="GOOD">
                                Good
                            </option>

                            <option value="DAMAGED">
                                Damaged
                            </option>

                            <option value="MISSING">
                                Missing
                            </option>

                        </select>

                    </div>


                    {/* =========================================
                    RETURNED BY
                ========================================= */}

                    <div className="col-md-3 mb-3">

                        <label>
                            Returned By
                        </label>

                        <input
                            className="form-control"
                            value={returnForm.returned_by || userName || ""}
                            readOnly
                        />

                    </div>


                    {/* =========================================
                    REMARKS
                ========================================= */}

                    <div className="col-md-6 mb-3">

                        <label>
                            Remarks
                        </label>

                        <input
                            className="form-control"
                            value={returnForm.remarks || ""}
                            onChange={e => setReturnForm(prev => ({
                                ...prev, remarks: e.target.value
                            }))}
                        />

                    </div>

                </div>


                <div className="alert alert-info">

                    <i className="fa fa-info-circle me-2"></i>

                    CSSD Set, patient, IP ID, Patient ID,
                    department and quantity are automatically
                    populated from the selected issued record.

                </div>


                <button
                    type="button"
                    className="btn btn-danger"
                    onClick={submitReturn}
                    disabled={!returnForm.issue_id}
                >

                    <i className="fa fa-rotate-left me-1"></i>

                    Record Return

                </button>

            </div>

        </div>)}

        {/* ================================================== */}
        {/* STOCK */}
        {/* ================================================== */}

        {tab === "stock" && (<>

            <div className="row mb-4">

                <div className="col-md-3">

                    <div className="card shadow-sm">

                        <div className="card-body">

                            <h6>
                                CSSD Sets
                            </h6>

                            <h2>
                                {sets.length}
                            </h2>

                        </div>

                    </div>

                </div>


                <div className="col-md-3">

                    <div className="card shadow-sm">

                        <div className="card-body">

                            <h6>
                                Instruments
                            </h6>

                            <h2>
                                {instruments.length}
                            </h2>

                        </div>

                    </div>

                </div>


                <div className="col-md-3">

                    <div className="card shadow-sm">

                        <div className="card-body">

                            <h6>
                                Sterilization Cycles
                            </h6>

                            <h2>
                                {sterilizations.length}
                            </h2>

                        </div>

                    </div>

                </div>


                <div className="col-md-3">

                    <div className="card shadow-sm">

                        <div className="card-body">

                            <h6>
                                CSSD Issues
                            </h6>

                            <h2>
                                {issues.length}
                            </h2>

                        </div>

                    </div>

                </div>

            </div>


            <DataGrid
                rows={instruments}
                columns={instrumentColumns}
                autoHeight
                slots={{
                    toolbar: GridToolbar
                }}
                pageSizeOptions={[10, 25, 50]}
            />

        </>)}


        {/* ================================================== */}
        {/* HISTORY */}
        {/* ================================================== */}

        {tab === "history" && (<>

            <h5 className="mb-3">

                CSSD Issue / Return History

            </h5>


            <DataGrid
                rows={issues}
                columns={issueColumns}
                autoHeight
                slots={{
                    toolbar: GridToolbar
                }}
                pageSizeOptions={[10, 25, 50]}
            />


            <hr className="my-5"/>


            <h5 className="mb-3">

                Sterilization History

            </h5>


            <DataGrid
                rows={sterilizations}
                columns={sterilizationColumns}
                autoHeight
                slots={{
                    toolbar: GridToolbar
                }}
                pageSizeOptions={[10, 25, 50]}
            />

        </>)}

    </section>);
}
export default CSSDManagement;